// src/components/chat/useTutorRuntime.js — assistant-ui runtime
// adapter using @assistant-ui/react-langgraph's `useLangGraphRuntime`.
//
// M3 swap (2026-05-05): replaces our hand-rolled `useExternalStoreRuntime`
// adapter with the LangGraph-native runtime. The runtime owns:
//   - message buffer + streaming reconciliation
//   - cancellation (composer stop button → AbortSignal)
//   - tool-call rendering hooks (M5 will register tool UIs here)
//   - interrupts (M4)
//
// We provide:
//   - `stream` — translates our existing /tutor/message/stream SSE
//     events into LangGraph stream events (messages/partial). Backend
//     stays the same; this hook is the wire-format adapter.
//   - external state for proposal / pending_action — these come back
//     in our SSE `final` event and feed the panel UI (until M4 makes
//     them interrupts).
//
// Returns: { runtime, sessionId, proposal, pendingAction, error,
//   resetCapture }.  TutorPanel reads these to render proposal /
//   pending-action cards alongside the thread.
import { useLangGraphRuntime } from '@assistant-ui/react-langgraph';
import { useCallback, useEffect, useRef, useState } from 'react';

import { apiService, handleApiError } from '../../services/api';
import {
  INITIAL_STATUS_STATE,
  abortCloseout,
  applyFinalCapture,
  buildFinalEmissions,
  clearStatusEmission,
  emissionForNarration,
  statusEmissionForUpdates,
  streamCloseout,
} from './finalEmissions';
import { streamSSEEvents } from '../../services/streaming';
import { useToast } from '../common/Toast';
import { dispatchUiSignal, makeUiSignalHandlers } from './uiSignals';

function extractText(content) {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((p) => (p?.type === 'text' || p?.type === 'text_delta' ? p.text : ''))
      .join('');
  }
  return '';
}

function newMessageId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `m_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

export default function useTutorRuntime({ bootContext, getRouteContext } = {}) {
  // Boot state — single session for the whole app session. We don't
  // wire `create`/`load` callbacks because we don't have a thread
  // switcher UI yet; one persistent thread is enough.
  const sessionIdRef = useRef(null);
  const [sessionId, setSessionId] = useState(null);
  const [bootError, setBootError] = useState(null);
  const [booting, setBooting] = useState(true);

  // Capture-tool side effects. Populated from each turn's `final` SSE
  // event; the panel reads them to render proposal / pending_action.
  const [proposal, setProposal] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingCurriculumEdit, setPendingCurriculumEdit] = useState(null);

  // ui_signal (issue #6) handler registry. `toast` is rebuilt each
  // render; keep the registry in a ref so the stream closure always
  // dispatches through the live toast object without re-subscribing.
  const toast = useToast();
  const uiSignalHandlersRef = useRef(null);
  uiSignalHandlersRef.current = makeUiSignalHandlers(toast);

  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let initial = null;
    try {
      initial = sessionStorage.getItem('tutor:initial');
      if (initial) sessionStorage.removeItem('tutor:initial');
    } catch {}
    (async () => {
      try {
        const res = await apiService.startTutor(
          initial || undefined,
          bootContext || undefined,
        );
        const s = res.data.session;
        sessionIdRef.current = s.session_id;
        setSessionId(s.session_id);
        // If start_session ran a turn (initial_message path), pull the
        // first proposal/pending_action so the panel reflects state.
        if (s.proposal) setProposal(s.proposal);
        if (s.pending_action) setPendingAction(s.pending_action);
        if (s.pending_curriculum_edit) setPendingCurriculumEdit(s.pending_curriculum_edit);
      } catch (err) {
        setBootError(handleApiError(err));
      } finally {
        setBooting(false);
      }
    })();
    // bootContext is intentionally boot-once; live updates flow through getRouteContext.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stream callback — runs each turn. Translates our SSE events into
  // LangGraph `messages/partial` events the runtime knows how to fold
  // into the visible thread. Side-channels proposal/pending_action via
  // setState (the runtime doesn't surface them; the panel does).
  const stream = useCallback(async function* (newMessages, config) {
    if (!sessionIdRef.current) {
      throw new Error('Tutor session not ready');
    }
    const last = newMessages[newMessages.length - 1];
    const text = extractText(last?.content);
    if (!text) return;

    const ctx = typeof getRouteContext === 'function' ? getRouteContext() : null;
    const messageId = newMessageId();
    let statusState = INITIAL_STATUS_STATE; // thinking / tool status line (see finalEmissions)
    let yieldedAnyText = false; // real (user-visible) content yielded — status lines don't count
    let sawFinal = false;
    // Always yield a FRESH object — the accumulator's default
    // appendMessage is `(_, curr) => curr`. If we yielded the same
    // mutated ref, downstream React child memoization could see
    // stale identity and skip re-rendering the bubble content.
    // Yield content as an array of parts (LangChain's complex form)
    // rather than a bare string. Either is valid per the type, but the
    // primitive content rendering path is happier with parts.
    const buildAi = (text) => ({
      id: messageId,
      type: 'ai',
      content: [{ type: 'text', text }],
    });

    for await (const evt of streamSSEEvents({
      path: '/api/v1/tutor/message/stream',
      body: {
        session_id: sessionIdRef.current,
        text,
        ...(ctx ? { context: ctx } : {}),
      },
      signal: config.abortSignal,
    })) {
      if (config.abortSignal?.aborted) {
        // Stop pressed mid-turn: clear any stale status line so the bubble
        // doesn't freeze on 'thinking…' / 'working on it…' (issue #111).
        const { state, emission } = abortCloseout(statusState, { messageId });
        statusState = state;
        if (emission) yield emission;
        return;
      }
      if (evt.type === 'error') throw new Error(evt.data?.message || 'stream error');
      if (evt.type === 'custom') {
        // Agent→UI side-channel (issue #6). Pure side effect: fire the
        // transient UI handler (toast/scroll/…) and move on. NEVER yield
        // a render emission here — these frames carry no chat content —
        // and never throw (dispatchUiSignal swallows bad frames with a
        // console.warn so a stray signal can't abort the turn render).
        dispatchUiSignal(uiSignalHandlersRef.current, evt.data);
        continue;
      }
      if (evt.type === 'updates') {
        // Long silent tool runs: surface one quiet status line so the
        // chat doesn't look dead (issue #102). Helper is idempotent — the
        // sub-agent flood of node updates never stacks lines.
        const { state, emission } = statusEmissionForUpdates(statusState, evt.data, { messageId });
        statusState = state;
        if (emission) yield emission;
        continue;
      }
      if (evt.type === 'messages' && evt.data?.text) {
        // Hidden thinking: every streamed delta is the model narrating to
        // itself (submit_reply contract — the real reply only arrives in
        // `final`). Never render it; show the quiet thinking line instead.
        const { state, emission } = emissionForNarration(statusState, { messageId });
        statusState = state;
        if (emission) yield emission;
        continue;
      }
      if (evt.type === 'final') {
        sawFinal = true;
        applyFinalCapture(evt.data, { setProposal, setPendingAction, setPendingCurriculumEdit });
        const finalEmissions = buildFinalEmissions({ messageId, finalData: evt.data, yieldedAnyText, buildAi });
        for (const out of finalEmissions) {
          yield out;
          yieldedAnyText = true;
        }
        // Clear the status line. If `final` produced replacement content it
        // already overwrote the bubble; otherwise emit an explicit clear.
        const { state, emission } = clearStatusEmission(statusState, {
          messageId,
          alreadyReplaced: finalEmissions.length > 0,
        });
        statusState = state;
        if (emission) yield emission;
      }
    }
    // Post-loop abort (stream ended naturally but the signal is aborted):
    // route through abortCloseout's quiet clear, NOT streamCloseout's
    // drop-notice (issue #111). Return before streamCloseout runs.
    if (config.abortSignal?.aborted) {
      const { state, emission } = abortCloseout(statusState, { messageId });
      statusState = state;
      if (emission) yield emission;
      return;
    }
    const closeout = streamCloseout({
      sawFinal,
      aborted: false,
      messageId,
    });
    if (closeout) yield closeout;
  }, [getRouteContext]);

  const runtime = useLangGraphRuntime({
    stream,
    unstable_allowCancellation: true,
  });

  // Clearing capture state — used by the panel after the user clicks
  // a propose_action button (we don't want a stale "open course X"
  // button hanging around after navigation).
  const resetCapture = useCallback(() => {
    setProposal(null);
    setPendingAction(null);
    setPendingCurriculumEdit(null);
  }, []);

  return {
    runtime,
    sessionId,
    proposal,
    pendingAction,
    pendingCurriculumEdit,
    error: bootError,
    booting,
    resetCapture,
  };
}
